import { StatusBadge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import type { StatusTimelineEntry } from "@/types/application";

function formatTimelineDate(value: string): { date: string; time: string } {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { date: "—", time: "" };
  }
  return {
    date: new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(parsed),
    time: new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(parsed),
  };
}

type StatusTimelineProps = {
  currentStatus: string;
  currentLabel: string;
  entries?: StatusTimelineEntry[] | null;
};

export function StatusTimeline({
  currentStatus,
  currentLabel,
  entries,
}: StatusTimelineProps) {
  const items = entries ?? [];

  return (
    <section className="rounded-2xl border border-line bg-surface-raised p-5 sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">Application status</h2>
          <p className="mt-1 text-sm text-muted">Updates visible for this application.</p>
        </div>
        <StatusBadge status={currentStatus} label={currentLabel} />
      </div>

      {items.length > 0 ? (
        <ol className="relative m-0 list-none pl-7">
          <span
            className="absolute bottom-1 left-[0.34rem] top-1 w-0.5 bg-line"
            aria-hidden
          />
          {items.map((entry, index) => {
            const { date, time } = formatTimelineDate(entry.occurred_at);
            const remarks = entry.remarks?.trim();
            return (
              <li
                key={`${entry.status}-${entry.occurred_at}-${index}`}
                className={cn("relative", index < items.length - 1 && "pb-5")}
              >
                <span
                  className="absolute -left-7 top-1.5 size-3 rounded-full border-2 border-brand bg-white"
                  aria-hidden
                />
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">{entry.status_display}</p>
                    {remarks ? (
                      <p className="mt-1 text-sm italic text-ink-soft">{remarks}</p>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-right text-xs text-muted">
                    <div>{date}</div>
                    {time ? <div>{time}</div> : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="text-sm text-muted">No status updates recorded yet.</p>
      )}
    </section>
  );
}
